import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/state/AuthContext';
import { useTheme } from '@/state/ThemeContext';
import { Icon, type IconName } from '@/components/ui';

interface DockNavItem {
  to: string;
  icon: IconName;
  label: string;
  end?: boolean;
}

const DOCK_ITEMS: DockNavItem[] = [
  { to: '/dashboard', icon: 'home', label: 'Tableau de bord', end: true },
  { to: '/dashboard/invoices', icon: 'invoices', label: 'Factures' },
  { to: '/dashboard/create', icon: 'plus', label: 'Nouvelle facture' },
  { to: '/dashboard/upload', icon: 'scan', label: 'Scan OCR' },
  { to: '/dashboard/clients', icon: 'mail', label: 'Clients' },
  { to: '/dashboard/archive', icon: 'file-generic', label: 'Archives' },
  { to: '/dashboard/settings', icon: 'cog', label: 'Paramètres' },
];

function initialsFromUser(user: { name?: string; email: string } | null): string {
  if (!user) return 'M';
  const source = user.name?.trim() || user.email;
  const bits = source.split(/[\s@.+_-]+/).filter(Boolean);
  if (bits.length === 0) return source[0]?.toUpperCase() ?? 'M';
  const first = bits[0][0] ?? '';
  const second = bits.length > 1 ? bits[1][0] ?? '' : '';
  return (first + second).toUpperCase() || 'M';
}

export function DashboardLayout() {
  const { user, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const nav = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (!showNotifications) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowNotifications(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [showNotifications]);

  async function handleLogout() {
    await signOut();
    nav('/auth', { replace: true });
  }

  return (
    <div className="neo-dash-root">
      {/* Sleek Mobile Top Bar (visible on phone screens) */}
      <header className="neo-mobile-topbar" aria-label="En-tête mobile">
        <NavLink to="/dashboard" className="neo-mobile-brand" title="Accueil Ma Facture">
          <span className="neo-mobile-brand-mark">M</span>
          <span className="neo-mobile-brand-name">Ma Facture</span>
        </NavLink>

        <div className="neo-mobile-actions">
          {/* Bouton Mode Clair / Sombre direct sur téléphone */}
          <button
            type="button"
            className="neo-mobile-action-btn"
            aria-label="Notifications"
            aria-expanded={showNotifications}
            onClick={() => setShowNotifications((s) => !s)}
            title="Notifications"
          >
            <Icon name="bell" size={18} />
          </button>

          <button
            type="button"
            className="neo-mobile-action-btn"
            onClick={toggleTheme}
            title={isDark ? 'Passer au mode clair' : 'Passer au mode sombre'}
            aria-label={isDark ? 'Mode clair' : 'Mode sombre'}
          >
            <Icon name={isDark ? 'sun' : 'moon'} size={18} />
          </button>

          {/* Bouton Déconnexion direct sur téléphone */}
          <button
            type="button"
            className="neo-mobile-action-btn neo-mobile-logout-btn"
            onClick={handleLogout}
            title="Se déconnecter de la session"
            aria-label="Se déconnecter"
          >
            <Icon name="logout" size={17} />
            <span className="neo-mobile-logout-label">Sortir</span>
          </button>

          {/* Avatar utilisateur vers Paramètres */}
          <NavLink
            to="/dashboard/settings"
            className="neo-mobile-avatar"
            title={user?.name || user?.email || 'Profil & Paramètres'}
          >
            <span>{initialsFromUser(user)}</span>
          </NavLink>
        </div>
      </header>

      <div className="neo-layout-container">
        {/* Sleek Floating Dock Sidebar */}
        <aside className="neo-dock" aria-label="Navigation">
          {/* Brand Mark Circle (Top) */}
          <NavLink to="/dashboard" className="neo-dock-brand" title="Accueil Ma Facture">
            <span>M</span>
          </NavLink>

          {/* Nav Items */}
          <nav className="neo-dock-nav">
            {DOCK_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `neo-dock-item ${isActive ? 'active' : ''}`
                }
                title={item.label}
              >
                <Icon name={item.icon} size={20} />
              </NavLink>
            ))}

            {/* Notification Bell with indicator */}
            <button
              type="button"
              className="neo-dock-item"
              title="Notifications"
              aria-label="Ouvrir les notifications"
              aria-expanded={showNotifications}
              onClick={() => setShowNotifications((s) => !s)}
              style={{ position: 'relative' }}
            >
              <Icon name="bell" size={20} />
              <span className="neo-dock-badge" />
            </button>
          </nav>

          {/* Bottom Dock: Theme Toggle, Logout & User Avatar */}
          <div className="neo-dock-bottom">
            <button
              type="button"
              className="neo-dock-item"
              title={isDark ? 'Passer au mode clair' : 'Passer au mode sombre'}
              onClick={toggleTheme}
              aria-label={isDark ? 'Mode clair' : 'Mode sombre'}
            >
              <Icon name={isDark ? 'sun' : 'moon'} size={19} />
            </button>

            <button
              type="button"
              className="neo-dock-item"
              title="Déconnexion"
              onClick={handleLogout}
            >
              <Icon name="logout" size={18} />
            </button>

            <NavLink
              to="/dashboard/settings"
              className="neo-user-avatar"
              title={user?.name || user?.email || 'Profil utilisateur'}
              aria-label={`Profil et paramètres${user?.name ? ` : ${user.name}` : ''}`}
            >
              <span>{initialsFromUser(user)}</span>
            </NavLink>
          </div>
        </aside>

        {/* Main Content View */}
        <main className="neo-main-area" style={{ flex: 1, minWidth: 0 }}>
          <Outlet />
        </main>
      </div>
      {showNotifications && createPortal(
        <div className="neo-notifications-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowNotifications(false); }}>
          <section className="neo-notifications-pop" role="dialog" aria-modal="true" aria-labelledby="notifications-title">
            <div className="neo-notifications-heading">
              <div><span className="neo-notifications-kicker">CENTRE DE MESSAGES</span><h2 id="notifications-title">Notifications</h2></div>
              <button type="button" className="neo-notifications-close" onClick={() => setShowNotifications(false)} aria-label="Fermer les notifications"><Icon name="x" size={18} /></button>
            </div>
            <div className="neo-notifications-list">
              <article className="neo-notification-item"><span className="neo-notification-icon"><Icon name="scan" size={18} /></span><div><strong>Extraction terminée</strong><p>Les données de votre document sont prêtes à être vérifiées.</p><time>À l’instant</time></div></article>
              <article className="neo-notification-item"><span className="neo-notification-icon"><Icon name="invoices" size={18} /></span><div><strong>Vérification des factures</strong><p>Votre espace est à jour. Consultez vos dernières factures.</p><time>Récemment</time></div></article>
            </div>
          </section>
        </div>,
        document.body,
      )}
    </div>
  );
}
